/**
 * Notification Preferences Component
 * User settings for auto-checkout notifications
 * 
 * Features:
 * - Toggle auto-checkout self notifications
 * - Toggle auto-checkout team notifications (managers/HR only)
 * - Real-time save with optimistic updates
 * - Accessible switch controls
 */

import { useEffect, useState } from 'react';
import { useNotificationPrefsStore } from '@/stores/notificationPrefsStore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Bell, BellSlash, Users } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useApp } from '@/context/AppContext';

export function NotificationPreferences() {
  const { preferences, loading, error, fetchPreferences, updatePreferences } = useNotificationPrefsStore();
  const { currentUser } = useApp();
  const [optimisticPrefs, setOptimisticPrefs] = useState({
    auto_checkout_self: 1,
    auto_checkout_team: 0,
  });

  // Fetch preferences on mount
  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  // Update optimistic state when preferences load
  useEffect(() => {
    if (preferences) {
      setOptimisticPrefs({
        auto_checkout_self: preferences.auto_checkout_self,
        auto_checkout_team: preferences.auto_checkout_team,
      });
    }
  }, [preferences]);

  // Check if user can receive team notifications
  const canReceiveTeamNotifications = currentUser && ['manager', 'hr', 'admin', 'superadmin'].includes(currentUser.role);

  // Handle toggle change
  const handleToggle = async (key: 'auto_checkout_self' | 'auto_checkout_team', value: boolean) => {
    const numericValue = value ? 1 : 0;
    
    // Optimistic update
    setOptimisticPrefs((prev) => ({
      ...prev,
      [key]: numericValue,
    }));

    try {
      await updatePreferences({ [key]: numericValue });
      toast.success('Notification preferences updated');
    } catch (err) {
      // Revert on error
      setOptimisticPrefs((prev) => ({
        ...prev,
        [key]: prev[key] === 1 ? 0 : 1,
      }));
      toast.error((err as Error).message || 'Failed to update preferences');
    }
  };

  if (loading && !preferences) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Notification Preferences</CardTitle>
          <CardDescription>Loading preferences...</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card role="region" aria-label="Notification Preferences">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5" weight="duotone" />
          <CardTitle>Auto-Checkout Notifications</CardTitle>
        </div>
        <CardDescription>
          Choose when you want to receive notifications about automatic checkouts
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Error Message */}
        {error && (
          <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded text-sm" role="alert">
            {error}
          </div>
        )}

        {/* Self Notifications */}
        <div className="flex items-start justify-between gap-4 p-4 border rounded-lg">
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-2">
              {optimisticPrefs.auto_checkout_self ? (
                <Bell className="h-5 w-5 text-primary" weight="fill" />
              ) : (
                <BellSlash className="h-5 w-5 text-muted-foreground" />
              )}
              <Label
                htmlFor="auto-checkout-self"
                className="text-base font-medium cursor-pointer"
              >
                Personal Auto-Checkout Notifications
              </Label>
            </div>
            <p className="text-sm text-muted-foreground">
              Get notified when you're automatically checked out at the end of your shift
            </p>
          </div>
          <Switch
            id="auto-checkout-self"
            checked={!!optimisticPrefs.auto_checkout_self}
            onCheckedChange={(checked) => handleToggle('auto_checkout_self', checked)}
            aria-label="Toggle personal auto-checkout notifications"
          />
        </div>

        {/* Team Notifications (Manager/HR only) */}
        {canReceiveTeamNotifications && (
          <div className="flex items-start justify-between gap-4 p-4 border rounded-lg">
            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-2">
                {optimisticPrefs.auto_checkout_team ? (
                  <Users className="h-5 w-5 text-primary" weight="fill" />
                ) : (
                  <Users className="h-5 w-5 text-muted-foreground" />
                )}
                <Label
                  htmlFor="auto-checkout-team"
                  className="text-base font-medium cursor-pointer"
                >
                  Team Auto-Checkout Notifications
                </Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Get notified when team members in your department are automatically checked out
              </p>
            </div>
            <Switch
              id="auto-checkout-team"
              checked={!!optimisticPrefs.auto_checkout_team}
              onCheckedChange={(checked) => handleToggle('auto_checkout_team', checked)}
              aria-label="Toggle team auto-checkout notifications"
            />
          </div>
        )}

        {/* Info Message */}
        <div className="bg-muted/50 border border-border px-4 py-3 rounded text-sm">
          <p className="text-muted-foreground">
            <strong className="text-foreground">Note:</strong> Notifications will be sent via in-app notifications. 
            Auto-checkout runs every 15 minutes to check for employees who need to be checked out.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
